import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, FolderOpen, Loader2, Plus, RefreshCcw } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/lib/api';
import { loadTodaySummary } from '@/lib/workflowData';
import { AppEmptyState, AppPage, AppPageHeader } from '@/components/layout/AppPage';
import WorkflowCompatibilityNotice from '@/components/layout/WorkflowCompatibilityNotice';
import {
  MatterCompactAction,
  MatterContinueCard,
  MatterHomeSection,
  MatterPrimaryAction,
} from '@/components/matter/MatterHomePanel';

function LoadingState() {
  return (
    <section
      style={{
        border: '1px solid var(--border-subtle)',
        background: 'var(--bg-surface)',
        borderRadius: '24px',
        padding: '18px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '10px',
        color: 'var(--text-secondary)',
      }}
    >
      <Loader2 size={16} className="animate-spin" />
      Loading matter home...
    </section>
  );
}

export default function TodayPage() {
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadNotice, setLoadNotice] = useState('');

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const { data, notice } = await loadTodaySummary({
        apiBase: API,
        headers: getAuthHeaders(),
        user,
      });
      setSummary(data);
      setLoadNotice(notice);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, user]);

  useEffect(() => {
    document.title = 'Matter Home — Advoq';
    loadSummary();
  }, [loadSummary]);

  const matterHome = summary?.matter_home || {};
  const activeMatters = matterHome.active_matters || [];
  const recentMatters = matterHome.recent_matters || [];
  const draftsInProgress = matterHome.drafts_in_progress || [];
  const resumeLastWork = matterHome.resume_last_work || null;
  const startNewMatter = matterHome.start_new_matter || { href: '/war-room', cta: 'Start new matter' };

  const navigateTo = (href) => {
    if (href) navigate(href);
  };

  return (
    <main className="app-main" data-testid="today-page">
      <AppPage className="app-page--matter-hub app-page--landing-locked app-page--landing-expanded">
        <AppPageHeader
          eyebrow="Matters"
          title="Matter home"
          description="Pick up the next move."
          actions={(
            <button type="button" className="app-chat-button" onClick={loadSummary} disabled={loading}>
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />}
              Refresh
            </button>
          )}
        />

        <div style={{ display: 'grid', gap: '16px' }}>
          <WorkflowCompatibilityNotice message={loadNotice} />

          {loading && !summary ? <LoadingState /> : null}

          <section
            data-testid="matter-home-layout"
            className="app-split-grid"
            style={{
              gap: '16px',
              alignItems: 'start',
            }}
          >
            <div style={{ display: 'grid', gap: '16px', minWidth: 0 }}>
              <MatterHomeSection
                title="Active matters"
                count={activeMatters.length}
                dataTestId="matter-home-section-active"
                action={(
                  <button type="button" className="app-chat-button" onClick={() => navigate('/matters/dashboard')}>
                    All matters
                  </button>
                )}
              >
                {activeMatters.length ? (
                  <div style={{ display: 'grid', gap: '12px', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
                    {activeMatters.map((matter) => (
                      <MatterContinueCard
                        key={matter.matter_id || matter.title}
                        matter={matter}
                        onContinue={() => navigateTo(matter.continue_href)}
                        dataTestId={`matter-card-${matter.matter_id || matter.title}`}
                      />
                    ))}
                  </div>
                ) : (
                  <AppEmptyState
                    icon={FolderOpen}
                    title="No active matters"
                    description="Start a matter to begin."
                    action={(
                      <button type="button" className="advoq-btn-primary" onClick={() => navigateTo(startNewMatter.href)}>
                        <Plus size={14} />
                        {startNewMatter.cta}
                      </button>
                    )}
                  />
                )}
              </MatterHomeSection>

              <div className="app-detail-grid" style={{ gap: '16px' }}>
                <MatterHomeSection
                  title="Recent matters"
                  count={recentMatters.length}
                  dataTestId="matter-home-section-recent"
                >
                  {recentMatters.length ? (
                    <div style={{ display: 'grid', gap: '10px' }}>
                      {recentMatters.map((matter) => (
                        <MatterCompactAction
                          key={`recent_${matter.matter_id || matter.title}`}
                          item={matter}
                          onOpen={() => navigateTo(matter.continue_href)}
                          dataTestId={`recent-matter-${matter.matter_id || matter.title}`}
                        />
                      ))}
                    </div>
                  ) : (
                    <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
                      No recent matters.
                    </div>
                  )}
                </MatterHomeSection>

                <MatterHomeSection
                  title="Drafts in progress"
                  count={draftsInProgress.length}
                  dataTestId="matter-home-section-drafts"
                >
                  {draftsInProgress.length ? (
                    <div style={{ display: 'grid', gap: '10px' }}>
                      {draftsInProgress.map((draft) => (
                        <MatterCompactAction
                          key={draft.id}
                          item={draft}
                          onOpen={() => navigateTo(draft.href)}
                          dataTestId={`draft-item-${draft.id}`}
                        />
                      ))}
                    </div>
                  ) : (
                    <AppEmptyState
                      icon={FileText}
                      title="No drafts in progress"
                      description="Drafts will appear here once you save one."
                    />
                  )}
                </MatterHomeSection>
              </div>
            </div>

            <div style={{ display: 'grid', gap: '16px', minWidth: 0 }}>
              {resumeLastWork ? (
                <MatterPrimaryAction
                  eyebrow="Resume last work"
                  title={resumeLastWork.title || 'Resume'}
                  summary={resumeLastWork.summary || 'Continue where you left off.'}
                  cta={resumeLastWork.cta || 'Continue'}
                  onClick={() => navigateTo(resumeLastWork.href)}
                  dataTestId="resume-last-work-action"
                />
              ) : null}

              <MatterPrimaryAction
                eyebrow="New matter"
                title="Start new matter"
                summary="Open a fresh matter and keep the work in one place."
                cta={startNewMatter.cta || 'Start new matter'}
                onClick={() => navigateTo(startNewMatter.href)}
                dataTestId="start-new-matter-action"
              />
            </div>
          </section>
        </div>
      </AppPage>
    </main>
  );
}
